import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doSetupEngageDocumentUpdate } from 'store/remote/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual } from 'lodash';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { PageHeader } from 'components/PageHeader';
import { GapSmall } from 'components/GapSmall';

export const EngageSettingsDocumentModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsEngageDocument>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const { document } = route.params;

  const dataRef = React.useRef(cloneDeep(document));
  const isDirty = React.useCallback(
    () => !isEqual(document, dataRef.current),
    [document],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleSave = React.useCallback(
    () => dispatch(doSetupEngageDocumentUpdate({ document: dataRef.current })),
    [dispatch],
  );

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;
    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Engage Document"
          subtitle={document.name}
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <GapSmall>
        <Card label="Asset Page">
          <CardRow
            label="Show on Asset Page"
            value="Show a summary of this document on the asset page"
            labelAlignTop
            checkedValue={dataRef.current.enableAsset}
            onPress={() =>
              handleChangeValue(
                'enableAsset',
                !dataRef.current.enableAsset,
                true,
              )
            }
          />
        </Card>

        <Card>
          <CardRow
            label="Show Download Link"
            checkedValue={dataRef.current.enableDownload}
            onPress={() =>
              handleChangeValue(
                'enableDownload',
                !dataRef.current.enableDownload,
                true,
              )
            }
          />
          <CardRow
            label="Show Performed By"
            checkedValue={dataRef.current.enablePerformedBy}
            onPress={() =>
              handleChangeValue(
                'enablePerformedBy',
                !dataRef.current.enablePerformedBy,
                true,
              )
            }
          />
          <CardRow
            label="Show Review Date"
            checkedValue={dataRef.current.enableReviewDate}
            onPress={() =>
              handleChangeValue(
                'enableReviewDate',
                !dataRef.current.enableReviewDate,
                true,
              )
            }
          />
        </Card>
      </GapSmall>

      <Card label="Description">
        <TextAreaField
          placeholder="No Description"
          onChange={(value) => handleChangeValue('description', value)}
          value={dataRef.current.description}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusDelay
        />
      </Card>
    </Page>
  );
};
